<template>
  <div class="invoice-container">
    <el-container>
      <el-main>
        <!-- 订单选择区域 -->
        <div class="order-selection">
          <el-form :inline="true" :model="filterForm" class="filter-form">
            <el-form-item label="时间范围">
              <el-date-picker
                v-model="filterForm.dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="YYYY-MM-DD"
                @change="handleDateChange"
              />
            </el-form-item>
            <el-form-item label="发票状态">
              <el-select
                v-model="filterForm.invoiceStatus"
                placeholder="选择状态"
                clearable
                style="width: 120px"
              >
                <el-option
                  v-for="item in invoiceStatusOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleFilter">查询</el-button>
              <el-button @click="resetFilter">重置</el-button>
            </el-form-item>
          </el-form>

          <!-- 订单列表 -->
          <el-table
            :data="filteredOrders"
            style="width: 100%"
            border
            @selection-change="handleSelectionChange"
          >
            <el-table-column
              type="selection"
              width="55"
              :selectable="isSelectable"
            />
            <el-table-column prop="orderId" label="订单编号" min-width="160" />
            <el-table-column
              prop="createTime"
              label="创建时间"
              min-width="160"
            />
            <el-table-column prop="status" label="订单状态" min-width="100">
              <template #default="scope">
                <el-tag :type="getStatusType(scope.row.status)">
                  <!-- {{ scope.row.status }} -->
                  已完成
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="totalPrice" label="金额" min-width="100">
              <template #default="scope">
                ¥{{ scope.row.totalPrice.toFixed(2) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="invoice_status"
              label="发票状态"
              min-width="100"
            >
              <template #default="scope">
                <el-tag :type="getInvoiceStatusType(scope.row.invoice_status)">
                  {{ getInvoiceStatusText(scope.row.invoice_status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120" fixed="right">
              <template #default="scope">
                <el-button
                  v-if="scope.row.invoice_status === 2"
                  type="primary"
                  size="small"
                  @click="handleDownloadInvoice(scope.row)"
                >
                  下载发票
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 发票信息填写 -->
        <div class="invoice-form" v-if="selectedOrders.length > 0">
          <h2>发票信息</h2>
          <el-form
            ref="invoiceForm"
            :model="invoiceForm"
            :rules="invoiceRules"
            label-width="120px"
          >
            <el-form-item label="发票类型" prop="type">
              <el-radio-group v-model="invoiceForm.type">
                <el-radio label="personal">个人</el-radio>
                <el-radio label="company">企业</el-radio>
              </el-radio-group>
            </el-form-item>

            <template v-if="invoiceForm.type === 'personal'">
              <el-form-item label="发票抬头" prop="title">
                <el-select
                  v-model="invoiceForm.title"
                  filterable
                  allow-create
                  placeholder="请选择或输入个人发票抬头"
                  @change="handleTitleChange"
                >
                  <el-option
                    v-for="item in invoiceTitleOptions"
                    :key="item.invoice_id"
                    :label="item.title"
                    :value="item.title"
                  />
                </el-select>
              </el-form-item>
            </template>

            <template v-else>
              <el-form-item label="发票抬头" prop="title">
                <el-select
                  v-model="invoiceForm.title"
                  filterable
                  allow-create
                  placeholder="请选择或输入企业发票抬头"
                  @change="handleTitleChange"
                >
                  <el-option
                    v-for="item in invoiceTitleOptions"
                    :key="item.invoice_id"
                    :label="item.title"
                    :value="item.title"
                  />
                </el-select>
              </el-form-item>
              <el-form-item
                v-if="invoiceForm.type === 'company'"
                label="税号"
                prop="taxNumber"
              >
                <el-input
                  v-model="invoiceForm.taxNumber"
                  placeholder="请输入企业税号"
                />
              </el-form-item>
            </template>

            <el-form-item label="发票金额">
              <span class="total-amount">¥{{ totalAmount.toFixed(2) }}</span>
            </el-form-item>

            <el-form-item label="电子邮箱" prop="email">
              <el-input
                v-model="invoiceForm.email"
                placeholder="请输入接收发票的邮箱"
              />
            </el-form-item>

            <el-form-item>
              <el-button type="primary" @click="submitInvoice"
                >开具发票</el-button
              >
              <el-button @click="resetInvoiceForm">重置</el-button>
            </el-form-item>
          </el-form>
        </div>
      </el-main>
    </el-container>
  </div>
</template>

<script>
import axios from "axios";

const API_BASE_URL = "https://www.xiaoliangtongxue.cn/api"; // 后端API基础URL

const orderApi = {
  // 获取订单列表
  async getOrders(userId = null) {
    const url = userId
      ? `${API_BASE_URL}/order?user_id=${userId}`
      : `${API_BASE_URL}/order`;
    return await axios.get(url);
  },
};

const invoiceApi = {
  async getUserInvoiceTitles(userId) {
    return await axios.get(`${API_BASE_URL}/user/${userId}/invoices/titles`);
  },
  // 创建发票
  async createInvoice(data) {
    return await axios.post(`${API_BASE_URL}/invoice`, data);
  },

  // 获取发票列表
  async getInvoices(params) {
    return await axios.get(`${API_BASE_URL}/invoice`, { params });
  },

  // 获取发票详情
  async getInvoiceDetail(id) {
    return await axios.get(`${API_BASE_URL}/invoice/${id}`);
  },

  // 更新发票状态
  async updateInvoiceStatus(id, status) {
    return await axios.put(`${API_BASE_URL}/invoice/${id}/status`, { status });
  },
};

export default {
  name: "PrintInvoice",
  data() {
    return {
      invoiceTitleOptions: [], // 存储历史抬头和税号
      filterForm: {
        dateRange: [],
        invoiceStatus: "",
      },
      invoiceStatusOptions: [
        { label: "全部", value: "" },
        { label: "未开具", value: 0 },
        { label: "已申请", value: 1 },
        { label: "已开具", value: 2 },
      ],
      orderList: [],
      selectedOrders: [],
      invoiceForm: {
        type: "personal",
        title: "",
        taxNumber: "",
        email: localStorage.getItem("userEmail"),
      },
      invoiceRules: {
        type: [
          { required: true, message: "请选择发票类型", trigger: "change" },
        ],
        title: [{ required: true, message: "请输入发票抬头", trigger: "blur" }],
        taxNumber: [
          {
            required: true,
            message: "请输入纳税人识别号",
            trigger: "blur",
            validator: (rule, value, callback) => {
              if (this.invoiceForm.type === "company" && !value) {
                callback(new Error("企业发票必须提供税号"));
              } else {
                callback();
              }
            },
          },
        ],
        email: [
          { required: true, message: "请输入邮箱地址", trigger: "blur" },
          { type: "email", message: "请输入正确的邮箱地址", trigger: "blur" },
        ],
      },
    };
  },
  created() {
    this.fetchInvoiceTitles();
    this.fetchOrders();
  },
  computed: {
    filteredOrders() {
      let result = [...this.orderList];

      if (this.filterForm.dateRange && this.filterForm.dateRange.length === 2) {
        const [startDate, endDate] = this.filterForm.dateRange;
        result = result.filter((order) => {
          const orderDate = order.createTime.split(" ")[0];
          return orderDate >= startDate && orderDate <= endDate;
        });
      }

      if (this.filterForm.invoiceStatus !== "") {
        result = result.filter(
          (order) => order.invoice_status === this.filterForm.invoiceStatus
        );
      }

      return result;
    },
    totalAmount() {
      return this.selectedOrders.reduce(
        (sum, order) => sum + order.totalPrice,
        0
      );
    },
  },
  methods: {
    async handleTitleChange(title) {
      const matched = this.invoiceTitleOptions.find(
        (item) => item.title === title
      );
      if (matched) {
        this.invoiceForm.taxNumber = matched.tax_number || "";
      } else {
        this.invoiceForm.taxNumber = "";
      }
    },
    async fetchInvoiceTitles() {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const res = await invoiceApi.getUserInvoiceTitles(parseInt(userId));
        if (res.data.success) {
          this.invoiceTitleOptions = res.data.data || [];
        }
      } catch (err) {
        console.error("获取历史发票抬头失败:", err);
      }
    },
    // 获取订单列表
    async fetchOrders() {
      try {
        // 从localStorage获取用户ID
        const userId = localStorage.getItem("userId");

        if (!userId) {
          this.$message.error("请先登录");
          this.$router.push("/login");
          return;
        }

        // 获取用户订单
        const response = await orderApi.getOrders(parseInt(userId));
        if (response.data) {
          this.orderList = response.data
            .filter((order) => order.state === "completed") // 只保留已完成的订单
            .map((order) => ({
              orderId: order.id,
              createTime: order.create_time,
              status: order.state,
              totalPrice: order.pay,
              has_invoice: order.has_invoice,
              invoice_status: order.invoice_status || 0,
            }));
        }
      } catch (error) {
        console.error("获取订单列表失败:", error);
        this.$message.error(
          error.response?.data?.message || "获取订单列表失败"
        );
      }
    },
    handleDateChange() {
      this.selectedOrders = [];
    },
    handleFilter() {
      this.selectedOrders = [];
    },
    resetFilter() {
      this.filterForm = {
        dateRange: [],
        invoiceStatus: "",
      };
      this.selectedOrders = [];
    },
    handleSelectionChange(selection) {
      // 只选择未开具发票的订单
      this.selectedOrders = selection.filter(
        (order) => order.invoice_status === 0
      );
    },
    async submitInvoice() {
      try {
        const valid = await this.$refs.invoiceForm.validate();
        if (!valid) {
          return;
        }

        // 获取用户ID
        const userId = localStorage.getItem("userId");
        if (!userId) {
          this.$message.error("请先登录");
          this.$router.push("/login");
          return;
        }

        // 检查是否选择了订单
        if (this.selectedOrders.length === 0) {
          this.$message.warning("请选择需要开具发票的订单");
          return;
        }

        // 确认开具发票
        await this.$confirm(
          `确认开具发票？\n订单数量：${
            this.selectedOrders.length
          }\n发票金额：¥${this.totalAmount.toFixed(2)}`,
          "提示",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          }
        );

        // 准备发票数据
        const invoiceData = {
          order_ids: this.selectedOrders.map((order) => order.orderId),
          user_id: parseInt(userId),
          invoice_type: this.invoiceForm.type,
          title: this.invoiceForm.title,
          tax_number:
            this.invoiceForm.type === "company"
              ? this.invoiceForm.taxNumber
              : null,
          email: this.invoiceForm.email,
          amount: this.totalAmount,
        };

        // 提交发票申请
        const response = await invoiceApi.createInvoice(invoiceData);

        if (response.data.success) {
          this.$message.success(
            `成功申请发票，开具后会发送至您的邮箱，同时系统中也可下载`
          );

          // 重新获取订单列表以更新状态
          await this.fetchOrders();

          // 重置表单和选择
          this.resetInvoiceForm();
          this.selectedOrders = [];
        } else {
          this.$message.error(response.data.message || "发票开具失败");
        }
      } catch (error) {
        if (error === "cancel") {
          this.$message.info("已取消操作");
        } else {
          console.error("开具发票失败:", error);
          this.$message.error(
            error.response?.data?.message || "开具发票失败，请稍后重试"
          );
        }
      }
    },
    resetInvoiceForm() {
      this.$refs.invoiceForm.resetFields();
    },
    isSelectable(row) {
      // 只允许选择未开具发票的订单
      return row.invoice_status === 0;
    },
    getStatusType(status) {
      const statusMap = {
        打印中: "warning",
        配送中: "primary",
        已完成: "success",
        已取消: "info",
      };
      return statusMap[status] || "info";
    },
    getInvoiceStatusType(status) {
      const statusMap = {
        0: "info", // 未开具
        1: "warning", // 已申请
        2: "success", // 已开具
      };
      return statusMap[status] || "info";
    },
    getInvoiceStatusText(status) {
      const statusMap = {
        0: "未开具",
        1: "已申请",
        2: "已开具",
      };
      return statusMap[status] || "未知";
    },
    async handleDownloadInvoice(order) {
      try {
        // 获取该订单对应的发票信息
        const response = await axios.get(
          `${API_BASE_URL}/invoice?order_id=${order.orderId}`
        );

        console.log(response.data[0]);

        if (response.data && response.data.length > 0) {
          const invoice = response.data[0];

          console.log(invoice.id);

          // 下载发票文件
          window.open(`${API_BASE_URL}/download-invoice-file/${invoice.id}`);
          this.$message.success("发票下载已开始");
        } else {
          this.$message.error("未找到对应的发票信息");
        }
      } catch (error) {
        console.error("下载发票失败:", error);
        this.$message.error("下载发票失败，请稍后重试");
      }
    },
  },
};
</script>

<style scoped>
.invoice-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  text-align: center;
  margin-bottom: 30px;
  position: relative;
  padding-bottom: 16px;
}

.header h1 {
  color: #000000;
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 1px;
}

.order-selection {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.filter-form {
  margin-bottom: 20px;
}

.invoice-form {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.invoice-form h2 {
  margin-bottom: 20px;
  color: #000000;
  font-size: 18px;
  font-weight: 600;
}

.total-amount {
  font-size: 20px;
  color: #000000;
  font-weight: 600;
}

:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  --el-table-border-color: #000000;
  --el-table-header-bg-color: #ffffff;
}

:deep(.el-table--border::after),
:deep(.el-table--group::after),
:deep(.el-table::before) {
  background-color: #000000;
}

:deep(.el-table__inner-wrapper::before) {
  background-color: #000000;
}

:deep(.el-table__header th) {
  background-color: var(--el-table-header-bg-color);
  color: #000000;
  font-weight: 600;
  height: 50px;
  padding: 8px 0;
  text-align: center;
}

:deep(.el-table__body td) {
  padding: 12px 0;
  color: #333333;
  text-align: center;
}

:deep(.el-table__body tr:hover > td) {
  background-color: #f5f5f5 !important;
}

:deep(.el-table__body-wrapper) {
  overflow-x: auto !important;
}

:deep(.el-tag) {
  border-radius: 4px;
  padding: 4px 12px;
  font-weight: 500;
  border: 1px solid #000000;
  background-color: #ffffff;
  color: #000000;
}

:deep(.el-tag--warning) {
  background-color: #ffffff;
  color: #000000;
  border-color: #000000;
}

:deep(.el-tag--primary) {
  background-color: #ffffff;
  color: #000000;
  border-color: #000000;
}

:deep(.el-tag--info) {
  background-color: #ffffff;
  color: #000000;
  border-color: #000000;
}

:deep(.el-tag--success) {
  background-color: #ffffff;
  color: #000000;
  border-color: #000000;
}

:deep(.el-tag--danger) {
  background-color: #ffffff;
  color: #000000;
  border-color: #000000;
}

:deep(.el-button--primary) {
  background-color: #000000;
  border-color: #000000;
  color: #ffffff;
}

:deep(.el-button--primary:hover) {
  background-color: #333333;
  border-color: #333333;
}

:deep(.el-button--default) {
  background-color: #ffffff;
  border: 1px solid #000000;
  color: #000000;
}

:deep(.el-button--default:hover) {
  background-color: #000000;
  color: #ffffff;
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background-color: #000000;
  border-color: #000000;
}

:deep(.el-checkbox__inner:hover) {
  border-color: #000000;
}

:deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
  color: #000000;
}

:deep(.el-radio__input.is-checked .el-radio__inner) {
  border-color: #000000;
  background-color: #000000;
}

:deep(.el-radio__inner:hover) {
  border-color: #000000;
}

:deep(.el-radio__input.is-checked + .el-radio__label) {
  color: #000000;
}

:deep(.el-radio__inner::after) {
  background-color: #ffffff;
}

.el-main {
  display: flex;
  flex-direction: column;
}
</style>